<template>
  <BlogLayout>
    <Head :title="title" />
    <IContainer fluid class="_padding:0 _margin-bottom:4">
      <div class="header-bg _background:primary-dark">
        <IContainer>
          <IRow center>
            <IColumn>
              <div class="_padding-top:4 _padding-bottom:6">
                <h1 class="_text:light _text:normal _margin-bottom:0 responsive-title">
                  Available
                  <span class="_color:primary _text:semibold responsive-title-span">Courses</span>
                </h1>
                <p class="lead _text:light _text:normal _margin-top:1/4">
                  Elevate your climate career with expert-led foundational specializations and
                  targeted self-paced courses designed to build essential skills from industry
                  leaders.
                </p>
              </div>
            </IColumn>
          </IRow>
        </IContainer>
      </div>
    </IContainer>

    <div class="_display:flex">
      <IContainer>
        <div class="_margin-top:2">
          <!-- Search and Filter Controls -->
          <div class="_margin-bottom:3 _margin-top:1">
            <i-row start>
              <i-column lg="6" xs="12">
                <i-select
                  color="light"
                  v-model="filters.level"
                  class="_xs:margin-bottom:1 _lg:margin-bottom:0"
                  id="levelQuery"
                  name="levelQuery"
                  :options="levels"
                  placeholder="Select course level..."
                  size="lg"
                  clearable
                  @change="handleFilterChange" />
              </i-column>
              <i-column lg="6" xs="12">
                <i-input
                  color="light"
                  v-model="filters.search"
                  clearable
                  id="searchQuery"
                  name="searchQuery"
                  type="search"
                  placeholder="Search by course, tag or description..."
                  size="lg"
                  @input="debounceSearch">
                  <template #append>
                    <i-button color="primary">
                      <MagnifyingGlassIcon class="_display:inline-flex _color:white action-icon" />
                    </i-button>
                  </template>
                </i-input>
              </i-column>
            </i-row>
          </div>

          <!-- Course Results -->
          <div class="_max-height:100%">
            <!-- Course Cards -->
            <div v-if="displayedCourses.length >= 1">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 mb-8">
                <CourseCard
                  v-for="course in displayedCourses"
                  :key="course.id"
                  :course="course"
                  :courseImageUrl="courseImageUrl"
                  @navigate="navigateToCourse" />
              </div>
            </div>

            <!-- No Results State -->
            <div v-else-if="!loading">
              <ICard color="light" class="_margin-bottom:2">
                <template #header>
                  <div class="h4 _text:dark-35 _text:center">No Courses Found</div>
                </template>
                <div class="_padding:2">
                  <IListGroup :border="false">
                    <IListGroupItem>
                      <div class="_display:flex _justify-content:center">
                        <ExclamationCircleIcon class="_color:primary empty-icon" />
                      </div>
                      <p
                        v-if="hasActiveFilters"
                        class="_padding-top:1 _text:lg _text:dark-35 _text:center">
                        Sorry, your search query rendered no results.
                      </p>
                    </IListGroupItem>
                  </IListGroup>
                </div>
              </ICard>
            </div>

            <!-- Results Count -->
            <div class="_display:flex _justify-content:end" v-if="!loading">
              <div v-if="!hasActiveFilters">
                <p v-if="courses.total > displayedCourses.length" class="_text:light-30 _text:lg">
                  {{ displayedCourses.length }} of {{ courses.total }} results
                </p>
              </div>
              <div v-else>
                <p class="_text:light-30 _text:lg">
                  <span class="_text:bold">{{ displayedCourses.length }}</span>
                  result
                  <span v-if="displayedCourses.length !== 1">s</span>
                </p>
              </div>
            </div>

            <!-- Loading Skeleton -->
            <div class="_display:flex _justify-content:center _margin-bottom:1" v-if="loading">
              <el-skeleton :rows="2" animated />
            </div>

            <!-- Load More Button -->
            <div v-if="!hasActiveFilters && !loading" class="_display:flex _justify-content:center">
              <div v-if="courses.next_page_url">
                <div
                  class="button -primary -lg _xs:margin-top:1 _md:margin-top:0"
                  @click="loadMore"
                  :disabled="loadingMore">
                  <Square2StackIcon
                    class="_display:inline-flex _color:white _margin-right:1/3 action-icon" />
                  {{ loadingMore ? 'Loading...' : 'Load More' }}
                </div>
              </div>
            </div>

            <!-- No Records Message -->
            <p class="_text:light _text:md" v-if="courses.data.length === 0 && !loading">
              Sorry, there are no records to display.
            </p>
          </div>
        </div>
      </IContainer>
    </div>
  </BlogLayout>
</template>

<script setup>
  import { computed, ref, reactive, watch } from 'vue'
  import { Head, router } from '@inertiajs/vue3'
  import { useMeta } from '@/Composables/useMeta'
  import { Square2StackIcon } from '@heroicons/vue/24/solid'
  import { ExclamationCircleIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
  import BlogLayout from '@/Layouts/BlogLayout.vue'
  import CourseCard from '@/Components/CourseCard.vue'
  import _ from 'lodash'
  import config from '@config' // Make sure to import the config

  const props = defineProps({
    errors: Object,
    title: {
      type: String,
      default: 'Available Courses',
    },
    courses: Object,
    levels: Array,
    types: Array,
    fetchAmount: Number,
    isAuthenticated: Boolean,
    courseImageUrl: String,
    meta: Object,
  })

  // lodash call fix
  const debounce = _.debounce

  // Reactive state
  const loading = ref(false)
  const loadingMore = ref(false)
  const allCourses = ref([...props.courses.data])
  const nextPageUrl = ref(props.courses.next_page_url)

  const filters = reactive({
    search: '',
    level: null,
  })

  // Computed properties
  const hasActiveFilters = computed(() => {
    return filters.search.length > 0 || filters.level !== null
  })

  const displayedCourses = computed(() => {
    let filteredCourses = allCourses.value

    if (hasActiveFilters.value) {
      filteredCourses = allCourses.value.filter((course) => {
        // Level filter
        if (filters.level && filters.level.label) {
          const courseLevel = course.level?.label || ''
          if (!courseLevel.toLowerCase().includes(filters.level.label.toLowerCase())) {
            return false
          }
        }

        // Search filter
        if (filters.search) {
          const query = filters.search.toLowerCase()
          const searchableText = [
            course.name || '',
            course.description || '',
            course.level?.label || '',
            course.category || '',
            ...(course.tagged || []).map((tag) => tag.tag_name || tag.name || ''),
            // Handle enrollment count vs enrollments array
            course.enrollments_count ? `${course.enrollments_count} enrolled` : '',
          ]
            .join(' ')
            .toLowerCase()

          return searchableText.includes(query)
        }

        return true
      })
    }

    // Add promo information to each course
    return filteredCourses.map((course) => {
      const coursePromo = config.course_promotions?.courses?.[course.id]
      const hasPromotion =
        coursePromo &&
        coursePromo.enabled &&
        (!coursePromo.expires_at || new Date(coursePromo.expires_at) > new Date())
      const displayPrice = hasPromotion ? coursePromo.promo_price_usd : course.price

      return {
        ...course,
        enrollments_count:
          course.enrollments_count || (course.enrollments ? course.enrollments.length : 0),
        hasPromotion,
        displayPrice,
        promoText: coursePromo?.promo_text,
      }
    })
  })

  // Navigation function
  const navigateToCourse = (slug) => {
    router.visit(route('page.course.show', slug))
  }

  // Route helper function
  const route = (name, params = {}) => {
    const routes = {
      'courses.show': `/dashboard/courses/${params}`,
      'page.course.show': `/page/courses/${params}`,
      'page.courses': '/page/courses',
    }
    return routes[name] || '/'
  }

  // Debounced search function
  const debounceSearch = debounce(() => {
    // Search is handled by computed property, no need for additional logic
  }, 300)

  // Handle filter changes
  const handleFilterChange = () => {
    // Filters are reactive and handled by computed property
  }

  // Load more functionality for pagination
  const loadMore = async () => {
    if (!nextPageUrl.value || loadingMore.value) return

    loadingMore.value = true

    try {
      // Use Inertia's visit with preserveState to load more data
      router.visit(nextPageUrl.value, {
        method: 'get',
        preserveState: true,
        preserveScroll: true,
        only: ['courses'],
        onSuccess: (page) => {
          const newCourses = page.props.courses
          allCourses.value.push(...newCourses.data)
          nextPageUrl.value = newCourses.next_page_url
        },
        onFinish: () => {
          loadingMore.value = false
        },
      })
    } catch (error) {
      console.error('Error loading more courses:', error)
      loadingMore.value = false
    }
  }

  // Set meta data
  const { setMeta } = useMeta()
  if (props.meta) {
    setMeta(props.meta)
  }
</script>

<style scoped>
  .header-bg {
    background-color: #ffffff;
    background-image: url('/images/grid_background_landing.svg');
    background-size: auto auto;
    background-repeat: no-repeat;
  }

  .responsive-title {
    font-size: 40px;
  }

  .responsive-title-span {
    font-size: 40px;
  }

  @media screen and (min-width: 768px) {
    .responsive-title,
    .responsive-title-span {
      font-size: 52px;
    }
  }

  .action-icon {
    width: 20px;
    height: 20px;
    stroke-width: 0.2em;
  }

  .empty-icon {
    position: relative;
    width: 64px;
    height: 64px;
  }

  .button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
